import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUserData } from "../config/redux/slices/userSlice/selectors";
import useDeviceId from "./useDeviceId";
import {
  subscribeDeviceToWebPush,
  unsubscribeDeviceFromWebPush,
} from "../adapters";
import {
  setNotificationsDisabledForDevice,
  setNotificationsEnabledForDevice,
} from "../config/redux/slices/userSlice";

const useNotificationsAccessHelper = () => {
  const dispatch = useDispatch();
  const device = useDeviceId();
  const user = useSelector(selectUserData);
  const { config } = user.data;
  const currentDeviceEnrolled = useMemo(
    () =>
      device.deviceId && config.notificationDevices?.includes(device.deviceId),
    [config, device]
  );

  const isNotificationSupported =
    "serviceWorker" in navigator &&
    "Notification" in window &&
    "PushManager" in window;

  const requestPermission = async () => {
    return await Notification.requestPermission();
  };

  const getSubscription = async () => {
    return await navigator.serviceWorker.ready.then(async (registration) => {
      return await registration.pushManager.getSubscription();
    });
  };

  const subscribe = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
    });
    return subscription;
  };

  const unsubscribe = async () => {
    const subscription = await getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
    if (currentDeviceEnrolled) {
      await unsubscribeDeviceFromWebPush(device.deviceId);
      dispatch(setNotificationsDisabledForDevice(device.deviceId));
    }
    return subscription;
  };

  const setupSubscription = async () => {
    if (Notification.permission !== "granted") {
      await requestPermission();
    }
    let subscription = await getSubscription();
    if (!subscription) {
      subscription = await subscribe();
      try {
        await subscribeDeviceToWebPush(device, subscription);
        dispatch(setNotificationsEnabledForDevice(device.deviceId));
      } catch (e) {
        subscription.unsubscribe();
      }
    }
    console.log(subscription);
  };

  return {
    isNotificationSupported,
    currentDeviceEnrolled,
    notificationStatus: useMemo(() =>
      isNotificationSupported ? Notification.permission : "denied"
    ),
    unsubscribe,
    setupSubscription,
  };
};

export default useNotificationsAccessHelper;
